import React from 'react';
import { Link } from 'react-router-dom';

interface DashboardCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path?: string;
  comingSoon?: boolean;
}

const dashboardCards: DashboardCard[] = [
  {
    title: 'Notification Performance',
    description: 'Track sent, opened, and attributed visits from business push notifications.',
    path: '/b2b/notification-metrics',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    ),
  },
  {
    title: 'User Insights',
    description: 'Live active-user count plus 1-minute sampled history with day, week, and month peak views.',
    path: '/b2b/user-insights',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.306a11.95 11.95 0 015.814-5.518l2.74-1.22m0 0l-5.94-2.281m5.94 2.28l-2.28 5.941" />
      </svg>
    ),
  },
  {
    title: 'Business Insights',
    description: 'Aggregated analytics across all onboarded businesses.',
    path: '/b2b/business-analytics',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    title: 'CRM',
    description: 'Venue pipeline, contacts, and outreach tracking.',
    path: '/b2b/crm',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a7.5 7.5 0 1115 0v.25H4.5v-.25z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 4l3 3m0-3l-3 3" />
      </svg>
    ),
  },
  {
    title: 'Event Reports',
    description: 'Generate partner-shareable PDF + CSV recaps for a date/time window.',
    path: '/b2b/event-reports',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  {
    title: 'Partner Engagement',
    description: 'Monitor partner activity, retention, and engagement trends.',
    comingSoon: true,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
      </svg>
    ),
  },
  {
    title: 'Deal Performance',
    description: 'Measure deal redemptions, revenue impact, and conversion rates.',
    comingSoon: true,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
      </svg>
    ),
  },
];

function B2BPage(): React.ReactElement {
  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-8">
        <h1 className="text-3xl font-bold text-white font-display">B2B Dashboards</h1>
        <p className="mt-2 text-gray-400 text-sm">
          Monitor and analyze business-facing metrics across the SocialSpark platform.
        </p>
      </div>

      {/* Card Grid */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {dashboardCards.map((card) => {
            const cardContent = (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    card.comingSoon
                      ? 'bg-gray-700/50 text-gray-500'
                      : 'bg-violet-600/20 text-violet-400'
                  }`}>
                    {card.icon}
                  </div>
                  {card.comingSoon && (
                    <span className="text-[11px] font-medium text-gray-500 bg-gray-700/50 px-2.5 py-1 rounded-full">
                      Coming soon
                    </span>
                  )}
                </div>
                <h3 className={`text-lg font-semibold mb-1.5 ${
                  card.comingSoon ? 'text-gray-500' : 'text-white'
                }`}>
                  {card.title}
                </h3>
                <p className={`text-sm leading-relaxed ${
                  card.comingSoon ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {card.description}
                </p>
                {!card.comingSoon && (
                  <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-violet-400">
                    View dashboard
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                )}
              </>
            );

            if (card.comingSoon) {
              return (
                <div
                  key={card.title}
                  className="bg-[#1a1a2e] border border-gray-800/50 rounded-xl p-6 opacity-60"
                >
                  {cardContent}
                </div>
              );
            }

            return (
              <Link
                key={card.title}
                to={card.path!}
                className="bg-[#1a1a2e] border border-violet-500/20 rounded-xl p-6 transition-all duration-200 hover:border-violet-500/40 hover:shadow-lg hover:shadow-violet-900/20 cursor-pointer no-underline"
              >
                {cardContent}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default B2BPage;
